const byName = (a, b) => a.name.localeCompare(b.name, 'fr');

// Value of the "Standard" option of the client filter, which keeps the PUBLIC collections; not a uuid, so that it
// never matches a client.
export const STANDARD_OPTION = 'standard';

// Options of the client filter, from the clients of GET /clients: each client without parent, by name, followed
// by its sub-clients, by name, one level down. The search text of a sub-client holds the name of its parent, so
// that searching a parent also lists its sub-clients. A sub-client whose parent is not listed stays at the top
// level, labelled and searched by its name followed by that of its parent in parentheses.
export const clientFilterOptions = (clients) => {
    const listed = new Set(clients.map(({ uuid }) => uuid));
    const isTopLevel = (client) => !client.parent || !listed.has(client.parent.uuid);
    const option = (client, depth, label, searchText = label) => ({ value: client.uuid, label, depth, searchText });

    return clients
        .filter(isTopLevel)
        .sort(byName)
        .flatMap((client) =>
            client.parent
                ? [option(client, 0, `${client.name} (${client.parent.name})`)]
                : [
                      option(client, 0, client.name),
                      ...clients
                          .filter((subClient) => subClient.parent?.uuid === client.uuid)
                          .sort(byName)
                          .map((subClient) => option(subClient, 1, subClient.name, `${subClient.name} ${client.name}`)),
                  ],
        );
};

// Uuids of the sub-clients of `uuid` among `clients`.
const subClientsOf = (clients, uuid) => clients.filter((client) => client.parent?.uuid === uuid).map((client) => client.uuid);

// Selection after a change of the client filter, from the `previous` selection to the `next` value of the filter:
// a parent that gets checked brings its sub-clients, a parent that gets unchecked takes its whole family away, and
// any other client is checked or unchecked alone. Each client appears once.
export const nextClientSelection = (clients, previous, next) => {
    const brought = next.filter((uuid) => !previous.includes(uuid)).flatMap((uuid) => subClientsOf(clients, uuid));
    const taken = previous.filter((uuid) => !next.includes(uuid)).flatMap((uuid) => subClientsOf(clients, uuid));
    return [...new Set([...next, ...brought])].filter((uuid) => !taken.includes(uuid));
};
